import { useMemo } from 'react';
import { useGetCatalogQuery, useSearchQuery } from '@platform/state/api';
import { useMappingCandidatesQuery } from './api/mappingApi';
import type { IMappingCandidates } from './IMappingCandidates';
import type { IMappingExcluded } from './IMappingExcluded';
import { mappingPlan } from './mappingPlan';

/** Скільки рядків показує панель (прототип: «Показано 30 з N»). */
export const MAPPING_LIMIT = 30;

/**
 * Записи для панелі «Зіставлення» (прототип v4 mapResults): без запиту — одразу перші 30 записів модулів простору (або вибраного типу)
 * у їх порядку, «з N» — усі ці записи, крім самого запису й уже зв'язаних (кожен — один раз); із запитом — пошук за кодом або назвою
 * в тих самих типах.
 */
export function useMappingCandidates(ws: string, scope: string, q: string, excluded: readonly IMappingExcluded[]): IMappingCandidates {
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws });
  const plan = useMemo(() => mappingPlan(catalog?.modules ?? [], scope), [catalog, scope]);
  // Типи в межах фільтра; поки каталог вантажиться — без обмеження.
  const allowed = useMemo(() => (catalog ? new Set(plan.types) : null), [catalog, plan]);
  const ids = useMemo(() => excluded.map((e) => e.id), [excluded]);
  const list = useMappingCandidatesQuery({ ws, types: plan.types, exclude: ids, limit: MAPPING_LIMIT }, { skip: Boolean(q) || !plan.types.length });
  const search = useSearchQuery({ q, workspaceId: ws, perGroup: MAPPING_LIMIT }, { skip: !q });
  return useMemo(() => {
    if (q) {
      // Групи пошуку — за типом або модулем: група без жодного збігу в межах фільтра не рахується, з частковими — без відкинутих.
      const kept = (search.data?.groups ?? []).map((g) => {
        const ok = g.hits.filter((h) => !ids.includes(h.recordId) && (!allowed || allowed.has(h.recordType)));
        return { ok, count: ok.length ? g.count - (g.hits.length - ok.length) : 0 };
      });
      const hits = kept.flatMap((k) => k.ok);
      const total = Math.max(hits.length, kept.reduce((n, k) => n + k.count, 0));
      return { rows: hits.slice(0, MAPPING_LIMIT).map((h) => ({ id: h.recordId, module: h.module, recordType: h.recordType, display: h.display, title: h.title, status: h.status })), total, loading: search.isFetching };
    }
    // Як у прототипі (linked — множина): запис, зв'язаний кількома типами зв'язку, віднімається один раз.
    const inScope = new Set(excluded.filter((e) => !allowed || allowed.has(e.recordType)).map((e) => e.id)).size;
    const rows = (list.data ?? []).map((r) => ({ id: r.id, module: r.module, recordType: r.recordType, display: r.display, title: r.title, status: r.status }));
    return { rows, total: Math.max(rows.length, plan.total - inScope), loading: list.isFetching || !catalog };
  }, [q, search.data, search.isFetching, ids, allowed, excluded, list.data, list.isFetching, plan.total, catalog]);
}
